import * as React from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useParams } from 'react-router';
import useNotifications from '../hooks/useNotifications/useNotifications';
import { getJobById, updateJobById } from '../../services/jobService';
import JobForm from './JobForm';
import PageContainer from './PageContainer';

// Front-end validation function
const validateJob = (values) => {
  const issues = [];

  if (!values.jobTitle || values.jobTitle.trim() === '') {
    issues.push({ path: ['jobTitle'], message: 'Job Title is required' });
  }

  if (!values.department || values.department.trim() === '') {
    issues.push({ path: ['department'], message: 'Department is required' });
  }

  if (!values.location || values.location.trim() === '') {
    issues.push({ path: ['location'], message: 'Location is required' });
  }

  if (values.minSalary && isNaN(values.minSalary)) {
    issues.push({ path: ['minSalary'], message: 'Minimum Salary must be a number' });
  }

  if (values.maxSalary && isNaN(values.maxSalary)) {
    issues.push({ path: ['maxSalary'], message: 'Maximum Salary must be a number' });
  }

  return { issues };
};

function JobEditForm({ initialValues, onSubmit }) {
  const { id } = useParams();
  console.log("Job ID:", id);
  const navigate = useNavigate();
  const notifications = useNotifications();

  const [formState, setFormState] = React.useState({
    values: initialValues,
    errors: {},
  });

  // 🔑 Sync form state with fetched job values
  React.useEffect(() => {
    setFormState({ values: initialValues, errors: {} });
  }, [initialValues]);

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = React.useCallback((newValues) => {
    setFormState((prev) => ({ ...prev, values: newValues }));
  }, []);

  const setFormErrors = React.useCallback((newErrors) => {
    setFormState((prev) => ({ ...prev, errors: newErrors }));
  }, []);

  const handleFieldChange = React.useCallback(
    (name, value) => {
      const newValues = { ...formValues, [name]: value };
      setFormValues(newValues);

      const { issues } = validateJob(newValues);
      setFormErrors({
        ...formErrors,
        [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
      });
    },
    [formValues, formErrors, setFormValues, setFormErrors]
  );

  const handleReset = React.useCallback(() => {
    setFormValues(initialValues);
  }, [initialValues, setFormValues]);

  const handleSubmit = React.useCallback(async () => {
    const { issues } = validateJob(formValues);
    if (issues?.length) {
      setFormErrors(Object.fromEntries(issues.map((i) => [i.path?.[0], i.message])));
      return;
    }
    setFormErrors({});

    try {
      await onSubmit(formValues);
      notifications.show('Job updated successfully.', {
        severity: 'success',
        autoHideDuration: 3000,
      });
      navigate('/app/jobs');
    } catch (err) {
      notifications.show(`Failed to update job. ${err.message}`, {
        severity: 'error',
        autoHideDuration: 3000,
      });
      throw err;
    }
  }, [formValues, onSubmit, navigate, notifications, setFormErrors]);

  return (
    <JobForm
      formState={formState}
      onFieldChange={handleFieldChange}
      onSubmit={handleSubmit}
      onReset={handleReset}
      submitButtonLabel="Save"
      backButtonPath={`/app/job/${id}`}
    />
  );
}

JobEditForm.propTypes = {
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default function JobEdit() {
  const { id } = useParams();
  const [job, setJob] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const loadJob = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getJobById(Number(id));
      const jobData = response.message; // ✅ unwrap API response

      // Ensure array fields exist for prefill
      setJob({
        ...jobData,
        responsibilities: jobData.responsibilities || [],
        requiredSkills: jobData.requiredSkills || [],
        preferredSkills: jobData.preferredSkills || [],
      });
    } catch (err) {
      setError(err);
    }
    setIsLoading(false);
  }, [id]);

  React.useEffect(() => {
    loadJob();
  }, [loadJob]);

  const handleSubmit = React.useCallback(
    async (values) => {
      const response = await updateJobById(Number(id), values);
      const jobData = response.message; // ✅ unwrap API response

      setJob({
        ...jobData,
        responsibilities: jobData.responsibilities || [],
        requiredSkills: jobData.requiredSkills || [],
        preferredSkills: jobData.preferredSkills || [],
      });
    },
    [id]
  );

  const renderContent = React.useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return <Alert severity="error">{error.message}</Alert>;
    }
    return job ? <JobEditForm initialValues={job} onSubmit={handleSubmit} /> : null;
  }, [isLoading, error, job, handleSubmit]);

  return (
    <PageContainer
      title={`Edit Job ${id}`}
      breadcrumbs={[
        { title: 'Jobs', path: '/app/jobs' },
        { title: `Job ${id}`, path: `/app/job/${id}` },
        { title: 'Edit' },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderContent}</Box>
    </PageContainer>
  );
}
